/**
 * tr-product-gallery — image principale + vignettes (ou grille).
 *
 * - Médias différés : vidéo / vidéo externe / modèle 3D sont rendus au clic
 *   depuis un <template> (rien de lourd au chargement).
 * - Modèles 3D : la librairie Shopify `model-viewer-ui` est chargée à la demande
 *   via `Shopify.loadFeatures` (plateforme, pas une dépendance tierce).
 * - Zoom : <dialog> natif (Escape, focus, backdrop gérés par le navigateur).
 * - Écoute tr:variant-change (document) filtré sur le produit : { featuredMediaId }.
 *
 * Méthode publique : showMedia(mediaId)
 */

import { TR_EVENTS } from './tr-events.js';

const ACTIVE_SLIDE = 'tr-gallery__slide--active';
const FILTERED_SLIDE = 'tr-gallery__slide--filtered';
const FILTERED_THUMB = 'tr-gallery__thumb--filtered';

class TrProductGallery extends HTMLElement {
  #slides = [];
  #thumbs = [];
  #controller = null;
  #modelUiLoaded = false;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-ref="gallerySlide"]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-ref="galleryThumb"]'));
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener(TR_EVENTS.VARIANT_CHANGE, this.#onVariantChange, { signal });

    this.#updateCounter();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /* ---------- public ---------- */

  showMedia(mediaId) {
    const id = String(mediaId);
    const target = this.#slides.find((slide) => slide.dataset.mediaId === id);
    if (target === undefined) return;

    for (const slide of this.#slides) {
      const match = slide === target;
      slide.classList.toggle(ACTIVE_SLIDE, match);
      if (match === false) this.#pauseMedia(slide);
    }

    for (const thumb of this.#thumbs) {
      thumb.setAttribute('aria-current', String(thumb.dataset.mediaId === id));
    }

    this.#updateCounter();
  }

  /* ---------- interne ---------- */

  /**
   * Compteur « 2/6 » : ne compte que les médias réellement visibles (le filtre
   * par variante peut en masquer). Décoratif — aria-hidden côté Liquid.
   */
  #updateCounter() {
    const index = this.querySelector('[data-ref="galleryIndex"]');
    if (index === null) return;

    const visible = this.#slides.filter((slide) => slide.classList.contains(FILTERED_SLIDE) === false);
    const active = this.#slides[this.#activeIndex()];
    const position = visible.indexOf(active);

    index.textContent = String(position < 0 ? 1 : position + 1);

    const count = this.querySelector('[data-ref="galleryCount"]');
    if (count !== null) count.textContent = String(visible.length);
  }

  #activeIndex() {
    return this.#slides.findIndex((slide) => slide.classList.contains(ACTIVE_SLIDE));
  }

  #step(delta) {
    const visible = this.#slides.filter((slide) => slide.classList.contains(FILTERED_SLIDE) === false);
    if (visible.length === 0) return;

    const active = this.#slides[this.#activeIndex()];
    const current = Math.max(0, visible.indexOf(active));
    const next = (current + delta + visible.length) % visible.length;
    const slide = visible[next];

    this.showMedia(slide.dataset.mediaId);

    const thumb = this.#thumbs.find((node) => node.dataset.mediaId === slide.dataset.mediaId);
    thumb?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  #onClick = (event) => {
    const thumb = event.target.closest('[data-ref="galleryThumb"]');
    if (thumb !== null) {
      event.preventDefault();
      this.showMedia(thumb.dataset.mediaId);
      return;
    }

    if (event.target.closest('[data-ref="galleryPrev"]') !== null) {
      this.#step(-1);
      return;
    }

    if (event.target.closest('[data-ref="galleryNext"]') !== null) {
      this.#step(1);
      return;
    }

    const poster = event.target.closest('[data-ref="mediaPoster"]');
    if (poster !== null) {
      this.#loadDeferred(poster);
      return;
    }

    const zoom = event.target.closest('[data-ref="zoomTrigger"]');
    if (zoom !== null) {
      this.#openZoom(zoom.dataset.mediaId);
      return;
    }

    if (event.target.closest('[data-ref="zoomClose"]') !== null) {
      this.querySelector('[data-ref="zoomDialog"]')?.close();
    }
  };

  #onKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.target.closest('[data-ref="galleryThumbs"]') === null) return;

    event.preventDefault();
    this.#step(event.key === 'ArrowRight' ? 1 : -1);

    const activeId = this.#slides[this.#activeIndex()]?.dataset.mediaId;
    this.#thumbs.find((node) => node.dataset.mediaId === activeId)?.focus();
  };

  #onVariantChange = (event) => {
    const detail = event.detail;
    if (detail === undefined) return;

    const productId = this.dataset.productId;
    if (detail.productId !== null && detail.productId !== undefined && String(detail.productId) !== productId) return;

    if (this.dataset.hideVariantMedia === 'true' && detail.variantId !== undefined) {
      this.#applyVariantFilter(detail.variantId);
    }

    const mediaId = detail.featuredMediaId;
    if (mediaId === undefined || mediaId === null) return;

    this.showMedia(mediaId);
  };

  /**
   * Masque les médias rattachés à d'AUTRES variantes que la variante courante
   * (05·R3) ; les médias communs (sans rattachement) restent visibles.
   */
  #applyVariantFilter(variantId) {
    const id = String(variantId);

    const filtered = (node) => {
      const attached = (node.dataset.variantIds ?? '').split(',').filter((value) => value !== '');
      return attached.length > 0 && attached.includes(id) === false;
    };

    for (const slide of this.#slides) slide.classList.toggle(FILTERED_SLIDE, filtered(slide));
    for (const thumb of this.#thumbs) thumb.classList.toggle(FILTERED_THUMB, filtered(thumb));

    this.#updateCounter();
  }

  /** Remplace le poster par le média réel contenu dans le <template> voisin. */
  #loadDeferred(poster) {
    const slide = poster.closest('[data-ref="gallerySlide"]');
    const template = slide?.querySelector('[data-ref="mediaTemplate"]');
    if (slide === null || slide === undefined || template === null || template === undefined) return;

    const content = template.content.cloneNode(true);
    poster.replaceWith(content);
    template.remove();
    slide.dataset.loaded = 'true';

    if (slide.dataset.mediaType === 'model') this.#setupModel(slide);

    const video = slide.querySelector('video');
    if (video !== null && typeof video.play === 'function') this.#play(video);
  }

  async #play(video) {
    try {
      await video.play();
    } catch {
      /* Lecture automatique refusée par le navigateur : les contrôles restent disponibles. */
    }
  }

  /** Chargement à la demande de l'UI Shopify pour <model-viewer>. */
  #setupModel(slide) {
    const viewer = slide.querySelector('model-viewer');
    if (viewer === null) return;

    const init = () => {
      if (window.Shopify?.ModelViewerUI === undefined) return;
      /* eslint-disable-next-line no-new -- l'UI Shopify s'attache au nœud. */
      new window.Shopify.ModelViewerUI(viewer);
    };

    if (this.#modelUiLoaded || window.Shopify?.ModelViewerUI !== undefined) {
      init();
      return;
    }

    if (typeof window.Shopify?.loadFeatures !== 'function') return;

    window.Shopify.loadFeatures(
      [{ name: 'model-viewer-ui', version: '1.0' }],
      (error) => {
        if (error) return;
        this.#modelUiLoaded = true;
        init();
      }
    );
  }

  #openZoom(mediaId) {
    const dialog = this.querySelector('[data-ref="zoomDialog"]');
    const image = this.querySelector('[data-ref="zoomFigure"] img');
    const slide = this.#slides.find((node) => node.dataset.mediaId === String(mediaId));

    if (dialog === null || image === null || slide === undefined) return;

    const source = slide.dataset.zoomSrc ?? '';
    if (source === '') return;

    image.removeAttribute('srcset');
    image.src = source;
    image.alt = slide.querySelector('img')?.alt ?? '';

    if (typeof dialog.showModal === 'function') dialog.showModal();
  }

  #pauseMedia(slide) {
    const video = slide.querySelector('video');
    if (video !== null && typeof video.pause === 'function') video.pause();
  }
}

if (customElements.get('tr-product-gallery') === undefined) {
  customElements.define('tr-product-gallery', TrProductGallery);
}
